import { apiRequest } from '@/services/http'

/**
 * `companyData` is a genuine snapshot, not a cache (see job.schema.ts's own comment): an external
 * (ATS-scraped) job's `companyId` usually names no row in `companies` at all — measured, not
 * assumed, on `job-search.service.ts`'s `LIST_FIELDS`: 3,271 of 4,822 active jobs are external,
 * and every one sampled had a dangling `companyId`. Resolving a company name/logo via `companyId`
 * alone leaves 68% of the feed blank; `external.companyData` is where the scraper actually put it.
 */
export interface JobExternal {
  applyUrl?: string
  /**
   * `socialLinks` is declared `String` on the schema, but every real row stores an OBJECT keyed
   * by a communication-type elId (`.lean()` returns the raw BSON, not the cast type) — see
   * `job-search.service.ts`'s `LIST_FIELDS` comment. Typed honestly as the map it actually is.
   */
  companyData?: { name?: string; logoUrl?: string; socialLinks?: Record<string, string> }
}

/** `core-service`'s talent-readable job feed — community jobs only, `status: 'active'` only. */
export interface JobSearchItem {
  /** Only on `interested` items: the recruiter's lead process, what Decline withdraws. */
  processId?: string
  _id: string
  slug: string
  title: string
  companyId: string
  location?: { cityId?: number; country?: string; region?: number; locationRegion?: number }
  workspaceType?: number
  positionType?: number[]
  seniorityLevel?: number[]
  salary?: { amount?: number; estimation?: number; type?: number; currency?: number }
  status: string
  createdAt: string
  external?: JobExternal
}

export interface JobSearchDetail extends JobSearchItem {
  description?: string
  roles?: number[]
  mainRole?: number
  skills?: number[]
  mandatorySkills?: number[]
  secondarySkills?: number[]
  industries?: number[]
  languages?: number[]
  degreeTypes?: number[]
  degreeNames?: number[]
  projectType?: number[]
  yearsOfExperience?: number
  numberOfPeopleManaged?: number
  monetaryScalesManaged?: number
  hasSecurityClearance?: boolean
  flags?: { isNewJob?: boolean; isHot?: boolean; isExternal?: boolean }
  updatedAt: string
}

export interface SearchJobsParams {
  /** Legacy FiltersPanel "Seniority" (seniorityLevel elIds). */
  seniority?: number[] | undefined
  /** Prefix search on the job title — matches the talent API's own param name. */
  term?: string
  companyId?: string
  // `| undefined` explicitly: callers pass these straight from a `toNumberList()`-style helper
  // that returns `undefined` for "no filter" rather than `[]`, and `exactOptionalPropertyTypes`
  // treats "absent" and "present but undefined" as different types.
  cities?: number[] | undefined
  positionTypes?: number[] | undefined
  industries?: number[] | undefined
  degreeTypes?: number[] | undefined
  degreeNames?: number[] | undefined
  /** `'date'` forces recency order even when `term` is set — omit (or `'relevance'`) for the
   * default (relevance when there's a term to score against, recency otherwise). */
  sort?: 'relevance' | 'date'
  limit?: number
  offset?: number
}

export function searchJobs(params: SearchJobsParams): Promise<{
  items: JobSearchItem[]
  total: number
  limit: number
  offset: number
}> {
  const search = new URLSearchParams()
  if (params.term) search.set('term', params.term)
  if (params.companyId) search.set('companyId', params.companyId)
  if (params.cities?.length) search.set('cities', params.cities.join('|'))
  if (params.positionTypes?.length) search.set('positionTypes', params.positionTypes.join('|'))
  if (params.industries?.length) search.set('industries', params.industries.join('|'))
  if (params.degreeTypes?.length) search.set('degreeTypes', params.degreeTypes.join('|'))
  if (params.degreeNames?.length) search.set('degreeNames', params.degreeNames.join('|'))
  if (params.seniority?.length) search.set('seniority', params.seniority.join('|'))
  if (params.sort === 'date') search.set('sort', 'date')
  if (params.limit !== undefined) search.set('limit', String(params.limit))
  if (params.offset !== undefined) search.set('offset', String(params.offset))
  const query = search.toString()
  return apiRequest(`/v1/job-search${query ? `?${query}` : ''}`)
}

export function getJobBySlug(slug: string): Promise<JobSearchDetail> {
  return apiRequest(`/v1/job-search/${slug}`)
}

/**
 * The real "Recommended" feed — `match_pairs` for the caller's own profile (talent-bff resolves
 * `profileId` server-side, see that module's comment), not a generic "all active jobs" list.
 * Empty for a profile the matcher hasn't scored yet — that's real, not a fetch failure.
 */
export function getRecommendedJobs(params: { limit?: number }): Promise<{
  items: JobSearchItem[]
  total: number
  limit: number
  offset: number
}> {
  const search = new URLSearchParams()
  if (params.limit !== undefined) search.set('limit', String(params.limit))
  const query = search.toString()
  return apiRequest(`/v1/job-search/recommended${query ? `?${query}` : ''}`)
}

/**
 * Legacy's "Interested in You" — a RECRUITER'S action (a `process` at `lead_approved`), not an
 * automated match. Real only for a candidate who has claimed/linked their recruiting profile
 * (`profile.talent.profileId`); empty otherwise, same as `getRecommendedJobs` being empty for a
 * profile the matcher hasn't scored yet — that's real, not a fetch failure.
 */
export function getInterestedJobs(params: { limit?: number }): Promise<{
  items: JobSearchItem[]
  total: number
  limit: number
  offset: number
}> {
  const search = new URLSearchParams()
  if (params.limit !== undefined) search.set('limit', String(params.limit))
  const query = search.toString()
  return apiRequest(`/v1/job-search/interested${query ? `?${query}` : ''}`)
}

/**
 * The body `saved-jobs`/`applications` both take — a SNAPSHOT the caller sends (see
 * `jobSnapshotRequest`'s own comment): the FE already has this data in hand from `job-search`,
 * and talent-service must never read `bs-core` to re-derive it.
 */
export interface JobSnapshotInput {
  jobId: string
  companyId: string
  fit?: number
  companyData?: { name?: string; logoUrl?: string }
  /** `slug` here is the JOB's slug — distinct from `SavedJob`/`Application`'s own `slug` field
   * (a `jd`+nanoid identifier for the saved/applied record itself), needed to link a card back
   * to `/job/:slug`. */
  jobData?: { title?: string; slug?: string; cityId?: number; country?: string; isExternal?: boolean; applyUrl?: string }
}

export interface SavedJob extends JobSnapshotInput {
  _id: string
  slug: string
  profileId: string
  createdAt: string
  lastUpdate?: string
}

export interface Application extends JobSnapshotInput {
  _id: string
  slug: string
  profileId: string
  processId?: string
  status: 'submitted' | 'viewed' | 'in_process' | 'interview' | 'offered' | 'hired' | 'on_hold' | 'rejected' | 'withdrawn'
  createdAt: string
  lastUpdate?: string
}

export function getSavedJobs(): Promise<{ items: SavedJob[] }> {
  return apiRequest('/v1/saved-jobs')
}
export function saveJob(input: JobSnapshotInput): Promise<{ savedJob: SavedJob }> {
  return apiRequest('/v1/saved-jobs', { method: 'POST', body: JSON.stringify(input) })
}
export function unsaveJob(jobId: string): Promise<{ ok: boolean }> {
  return apiRequest(`/v1/saved-jobs/${jobId}`, { method: 'DELETE' })
}

export function getApplications(): Promise<{ items: Application[] }> {
  return apiRequest('/v1/applications')
}
export function applyToJob(input: JobSnapshotInput): Promise<{ application: Application }> {
  return apiRequest('/v1/applications', { method: 'POST', body: JSON.stringify(input) })
}
/** Legacy `withdrawReason` metadata — the reasons a talent can pick when withdrawing or declining. */
export const WITHDRAW_REASONS: Array<{ value: string; label: string }> = [
  { value: 'not_interested', label: 'Not interested' },
  { value: 'offer_rejected', label: 'Offer rejected' },
  { value: 'not_active', label: 'Not looking right now' },
  { value: 'stayed_in_position', label: 'Stayed in current position' },
  { value: 'salary', label: 'Salary' },
  { value: 'no_show', label: 'Could not attend' },
  { value: 'accepted_another_offer', label: 'Accepted another offer' },
]

/** Legacy `PATCH /process/withdraw/:slug` — the reason travels to the recruiter-side process too. */
export function withdrawApplication(id: string, input: { reason?: string; comment?: string } = {}): Promise<{ application: Application; processClosed: boolean }> {
  return apiRequest(`/v1/applications/${id}/withdraw`, { method: 'PATCH', body: JSON.stringify(input) })
}

/** Legacy JobsContainer `onProcessDecline`: a recruiter's lead, declined — the same withdraw on that process. */
export function declineLead(processId: string, reason?: string): Promise<{ declined: boolean }> {
  return apiRequest('/v1/applications/decline-lead', { method: 'POST', body: JSON.stringify({ processId, ...(reason ? { reason } : {}) }) })
}

/** Legacy `GET /jobs/suggest?term=` — job-title typeahead (two characters minimum). */
export function suggestJobKeys(term: string): Promise<{ keys: string[] }> {
  if (term.trim().length < 2) return Promise.resolve({ keys: [] })
  return apiRequest(`/v1/job-search/suggest?term=${encodeURIComponent(term.trim())}`)
}

/**
 * A talent flagging a job posting.
 *
 * `reason` is an elId against the `reportReason` lookup category, NOT free text — five values on
 * this corpus (1 Offensive or Discriminatory · 2 Fake Job · 3 Inaccurate · 4 Advertisement ·
 * 5 Other), and the contract is `z.number().int()`, so a label string is a 400. Fetch the
 * vocabulary with `getLookup('reportReason')` and send the elId the talent picked.
 */
export interface JobReport {
  _id: string
  profileId: string
  jobId: string
  reason: number
  createdAt: string
}

/**
 * The caller's OWN reports — the list exists so the page can show "already reported" rather than
 * offering the action again. It is not a moderation feed; a talent can never read anyone else's.
 *
 * `_id` is in the response even though the controller selects only three fields: mongoose returns
 * it unless it is explicitly excluded. Typed here because it is there — verified against the
 * running service, not inferred from the `.select()`.
 */
export function getJobReports(): Promise<{ items: Array<Pick<JobReport, '_id' | 'jobId' | 'reason' | 'createdAt'>> }> {
  return apiRequest('/v1/job-reports')
}

/**
 * Reporting the same job twice UPDATES the reason rather than erroring or filing a second row —
 * the server upserts on its unique `{profileId, jobId}` index. So the caller may send this
 * whenever the talent submits the dialog, without first checking whether a report exists.
 */
export function createJobReport(input: { jobId: string; reason: number }): Promise<{ report: JobReport }> {
  return apiRequest('/v1/job-reports', { method: 'POST', body: JSON.stringify(input) })
}
